import { Injectable, Inject, forwardRef } from '@angular/core';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { WebSocketService, Message } from '../auth/web-socket.service';

export interface RCDataSyncEvent {
  type: 'contact' | 'template' | 'stage' | 'channel' | 'interview' | 'meeting';
  event: 'created' | 'updated' | 'deleted';
  id: string;
  company_id?: string;
  data?: any;
}

@Injectable({
  providedIn: 'root'
})
export class RCDataSyncService {
  private dataSyncSubject = new Subject<RCDataSyncEvent>();
  private isRCSuperAdmin = new BehaviorSubject<boolean>(false);
  private visibleCompanyIds = new BehaviorSubject<string[]>([]);
  private initialized = false;
  
  // General data sync stream
  public dataSyncUpdates$ = this.dataSyncSubject.asObservable();
  
  // Observable streams for different data types - lazy initialized
  private _contactUpdates$?: Observable<RCDataSyncEvent>;
  private _templateUpdates$?: Observable<RCDataSyncEvent>;
  private _stageUpdates$?: Observable<RCDataSyncEvent>;
  private _channelUpdates$?: Observable<RCDataSyncEvent>;
  private _interviewUpdates$?: Observable<RCDataSyncEvent>;
  private _meetingUpdates$?: Observable<RCDataSyncEvent>;

  constructor(private webSocketService: WebSocketService) {
    // Delay initialization to avoid circular dependency
    setTimeout(() => this.initialize(), 0);
  }

  private initialize(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.initializeWebSocketListener();
  }

  // Lazy getters for observables
  get contactUpdates$(): Observable<RCDataSyncEvent> {
    if (!this._contactUpdates$) {
      this._contactUpdates$ = this.getUpdatesForType('contact');
    }
    return this._contactUpdates$;
  }

  get templateUpdates$(): Observable<RCDataSyncEvent> {
    if (!this._templateUpdates$) {
      this._templateUpdates$ = this.getUpdatesForType('template');
    }
    return this._templateUpdates$;
  }

  get stageUpdates$(): Observable<RCDataSyncEvent> {
    if (!this._stageUpdates$) {
      this._stageUpdates$ = this.getUpdatesForType('stage');
    }
    return this._stageUpdates$;
  }

  get channelUpdates$(): Observable<RCDataSyncEvent> {
    if (!this._channelUpdates$) {
      this._channelUpdates$ = this.getUpdatesForType('channel');
    }
    return this._channelUpdates$;
  }

  get interviewUpdates$(): Observable<RCDataSyncEvent> {
    if (!this._interviewUpdates$) {
      this._interviewUpdates$ = this.getUpdatesForType('interview');
    }
    return this._interviewUpdates$;
  }

  get meetingUpdates$(): Observable<RCDataSyncEvent> {
    if (!this._meetingUpdates$) {
      this._meetingUpdates$ = this.getUpdatesForType('meeting');
    }
    return this._meetingUpdates$;
  }

  /**
   * Initialize WebSocket listener for RC data sync events
   */
  private initializeWebSocketListener(): void {
    this.webSocketService.messages$.subscribe((message: Message) => {
      if (this.isRCDataSyncMessage(message)) {
        const syncEvent = this.parseRCDataSyncEvent(message);
        if (syncEvent && this.shouldProcessEvent(syncEvent)) {
          console.log('[RCDataSync] Received sync event:', syncEvent);
          this.dataSyncSubject.next(syncEvent);
        }
      }
    });
  }

  /**
   * Check if message is an RC data sync event
   */
  private isRCDataSyncMessage(message: Message): boolean {
    return ['contact', 'template', 'stage', 'channel', 'interview', 'meeting'].includes(message.type) &&
           ['created', 'updated', 'deleted'].includes(message['event']);
  }

  /**
   * Parse WebSocket message into RC data sync event
   */
  private parseRCDataSyncEvent(message: Message): RCDataSyncEvent | null {
    try {
      const event: RCDataSyncEvent = {
        type: message.type as any,
        event: message['event'] as any,
        id: message['contact_id'] || message['template_id'] || message['stage_id'] || message['channel_id'] || message['interview_id'] || message['meeting_id'] || '',
        company_id: message['company_id'],
        data: message['data']
      };
      
      return event.id ? event : null;
    } catch (error) {
      console.error('[RCDataSync] Error parsing sync event:', error);
      return null;
    }
  }

  /**
   * Get updates stream for specific data type
   */
  private getUpdatesForType(type: RCDataSyncEvent['type']): Observable<RCDataSyncEvent> {
    return this.dataSyncUpdates$.pipe(
      filter(event => event.type === type)
    );
  }

  /**
   * Set whether current user is RC Super Admin
   */
  /**
   * Only act on events for a company this user can actually see.
   *
   * This service had NO such check while its near-identical twin
   * `mc-data-sync.service` did - the two are otherwise a rename-only copy. The
   * server already scopes the broadcast, so this was not a leak on its own; it
   * is the second layer, and the asymmetry between two files that do the same
   * job is how one of them ends up wrong.
   *
   * Empty `visibleCompanyIds` means "not told yet", which admits events rather
   * than silently blanking the RC dashboard - the caller sets it after the
   * hierarchy loads. A missing company_id on the EVENT is refused either way.
   */
  private shouldProcessEvent(event: RCDataSyncEvent): boolean {
    if (!event.company_id) return false;

    const visible = this.visibleCompanyIds.value;
    if (0 === visible.length) return true;

    return visible.includes(event.company_id);
  }

  public setRCSuperAdminStatus(isRCSuperAdmin: boolean, visibleCompanyIds: string[] = []): void {
    this.isRCSuperAdmin.next(isRCSuperAdmin);
    this.visibleCompanyIds.next(visibleCompanyIds);
  }

  /**
   * Get RC Super Admin status
   */
  public getRCSuperAdminStatus(): Observable<boolean> {
    return this.isRCSuperAdmin.asObservable();
  }

  /**
   * Manually trigger data refresh (useful for non-real-time updates)
   */
  public triggerDataRefresh(type: RCDataSyncEvent['type']): void {
    const refreshEvent: RCDataSyncEvent = {
      type,
      event: 'updated',
      id: 'manual-refresh',
      company_id: undefined
    };
    
    this.dataSyncSubject.next(refreshEvent);
  }

  /**
   * Check if current user should receive RC data sync events
   */
  public shouldReceiveRCSync(): boolean {
    return this.isRCSuperAdmin.value;
  }
}